import { apiRequest } from "@/features/api/apiClient";

export type AdminOverviewResponse = {
  ok: true;
  admin: {
    userId: string;
    email?: string;
  };
  overview: {
    totalUsers: number;
    totalProjects: number;
    totalBlueprints: number;
    totalToolResults: number;
  };
  message: string;
};

export type AdminUserSummary = {
  id: string;
  email: string;
  name: string;
  tier: "free" | "standard" | "pro";
  is_lifetime: boolean;
  is_admin: boolean;
  pro_subscription_status: string;
  standard_expiry_date: string | null;
  created_at: string;
  updated_at: string;
  monthly_blueprint_count: number;
  monthly_reset_date: string | null;
  total_blueprints_generated: number;
  projectCount: number;
  blueprintCount: number;
  toolResultCount: number;
};

export type AdminUserListResponse = {
  users: AdminUserSummary[];
  pagination: {
    page: number;
    pageSize: number;
    hasNextPage: boolean;
  };
};

export type AdminUserDetailResponse = {
  user: AdminUserSummary;
  access: {
    effectiveTier: "free" | "standard" | "pro";
    source: string;
    isLifetimeStandard: boolean;
    usage: {
      monthlyBlueprintCount: number;
      monthlyBlueprintLimit: number;
      remainingThisMonth: number;
    };
    capabilities: {
      canUseGuidedMode: boolean;
      canUseProTools: boolean;
      canExportPdf: boolean;
    };
  };
  counts: {
    projects: number;
    blueprints: number;
    generatedBlueprints: number;
    toolResults: number;
  };
  recentProjects: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
    updated_at: string;
  }>;
  recentBlueprints: Array<{
    id: string;
    title: string;
    status: string;
    generation_mode: string;
    target_builder: string;
    created_at: string;
    updated_at: string;
  }>;
  recentToolResults: Array<{
    id: string;
    tool_type: string;
    status: string;
    project_id: string | null;
    blueprint_id: string | null;
    created_at: string;
    updated_at: string;
  }>;
};

export type AdminAccessAdjustmentAction =
  | "grant_standard"
  | "grant_lifetime_standard"
  | "grant_pro"
  | "revoke_standard"
  | "revoke_lifetime_standard"
  | "revoke_pro"
  | "reset_to_free";

export type AdminAccessAdjustmentInput = {
  action: AdminAccessAdjustmentAction;
  reason: string;
  options?: {
    standardDurationDays?: number;
    proDurationDays?: number;
  };
};

export function getAdminOverview(token: string | null) {
  return apiRequest<AdminOverviewResponse>("/api/admin/overview", { token });
}

export function listAdminUsers(
  token: string | null,
  filters: { q?: string; tier?: string; page?: number; pageSize?: number } = {},
) {
  const params = new URLSearchParams();

  if (filters.q) {
    params.set("q", filters.q);
  }

  if (filters.tier) {
    params.set("tier", filters.tier);
  }

  if (filters.page) {
    params.set("page", filters.page.toString());
  }

  if (filters.pageSize) {
    params.set("pageSize", filters.pageSize.toString());
  }

  const query = params.toString();
  return apiRequest<AdminUserListResponse>(
    `/api/admin/users${query ? `?${query}` : ""}`,
    { token },
  );
}

export function getAdminUserDetail(token: string | null, userId: string) {
  return apiRequest<AdminUserDetailResponse>(
    `/api/admin/users/${encodeURIComponent(userId)}`,
    { token },
  );
}

export function adjustAdminUserAccess(
  token: string | null,
  userId: string,
  input: AdminAccessAdjustmentInput,
) {
  return apiRequest<AdminUserDetailResponse>(
    `/api/admin/users/${encodeURIComponent(userId)}/access-adjustment`,
    {
      method: "POST",
      token,
      body: input,
    },
  );
}

export type AdminPurchaseEventSummary = {
  id: string;
  provider: string;
  sourceLabel: string;
  eventId: string | null;
  transactionId: string | null;
  eventType: string;
  productId: string | null;
  priceId: string | null;
  email: string | null;
  userId: string | null;
  tierGranted: string | null;
  status: string;
  errorMessage: string | null;
  ghlSent: boolean;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdminLinkedUserSummary = {
  id: string;
  email: string;
  name: string;
  tier: "free" | "standard" | "pro";
  is_lifetime: boolean;
  is_admin: boolean;
  pro_subscription_status: string;
};

export type AdminPurchaseEventListResponse = {
  events: AdminPurchaseEventSummary[];
  pagination: {
    page: number;
    pageSize: number;
    hasNextPage: boolean;
  };
};

export type AdminReplayEligibility = {
  eligible: boolean;
  reason: string;
  riskLevel: string;
  supportedAction?: string;
};

export type AdminPurchaseEventDetailResponse = {
  event: AdminPurchaseEventSummary;
  linkedUser: AdminLinkedUserSummary | null;
  redactedPayload: Record<string, unknown> | null;
  eligibility: AdminReplayEligibility;
};

export function listAdminPurchaseEvents(
  token: string | null,
  filters: {
    q?: string;
    provider?: string;
    status?: string;
    eventType?: string;
    page?: number;
    pageSize?: number;
  } = {},
) {
  const params = new URLSearchParams();

  if (filters.q) params.set("q", filters.q);
  if (filters.provider) params.set("provider", filters.provider);
  if (filters.status) params.set("status", filters.status);
  if (filters.eventType) params.set("eventType", filters.eventType);
  if (filters.page) params.set("page", filters.page.toString());
  if (filters.pageSize) params.set("pageSize", filters.pageSize.toString());

  const query = params.toString();
  return apiRequest<AdminPurchaseEventListResponse>(
    `/api/admin/purchase-events${query ? `?${query}` : ""}`,
    { token },
  );
}

export function getAdminPurchaseEventDetail(token: string | null, eventId: string) {
  return apiRequest<AdminPurchaseEventDetailResponse>(
    `/api/admin/purchase-events/${encodeURIComponent(eventId)}`,
    { token },
  );
}

export type AdminPurchaseEventRetryInput = {
  reason: string;
  confirm: boolean;
};

export type AdminPurchaseEventRetryResponse = {
  originalEvent: AdminPurchaseEventSummary;
  retrySucceeded: boolean;
  errorMessage: string | null;
  auditEventId: string | null;
  message: string;
  updatedEligibility: AdminReplayEligibility;
};

export function retryAdminPurchaseEvent(
  token: string | null,
  eventId: string,
  input: AdminPurchaseEventRetryInput,
) {
  return apiRequest<AdminPurchaseEventRetryResponse>(
    `/api/admin/purchase-events/${encodeURIComponent(eventId)}/retry`,
    { method: "POST", token, body: input },
  );
}

// ============================================================================
// PROMO CODES
// ============================================================================

export type AdminPromoCodeSummary = {
  id: string;
  code: string;
  tierGranted: string;
  accessDurationDays: number | null;
  maxUses: number | null;
  usesCount: number;
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
  statusLabel: "Active" | "Inactive" | "Expired" | "Exhausted";
  remainingUses: number | null;
};

export type AdminPromoCodeUseSummary = {
  id: string;
  userId: string | null;
  usedAt: string;
  userEmail: string | null;
  userName: string | null;
  userTier: string | null;
};

export type AdminPromoCodeListResponse = {
  codes: AdminPromoCodeSummary[];
  pagination: {
    page: number;
    pageSize: number;
    hasNextPage: boolean;
  };
};

export type AdminPromoCodeDetailResponse = {
  code: AdminPromoCodeSummary;
  uses: AdminPromoCodeUseSummary[];
};

export type AdminPromoCodeCreateInput = {
  code: string;
  tierGranted: "standard" | "lifetime_standard" | "pro";
  accessDurationDays?: number | null;
  maxUses?: number | null;
  expiresAt?: string | null;
  active?: boolean;
};

export type AdminPromoCodeStatusInput = {
  reason: string;
};

export function listAdminPromoCodes(
  token: string | null,
  filters: { q?: string; tier?: string; active?: boolean; page?: number; pageSize?: number } = {},
) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.tier) params.set("tier", filters.tier);
  if (filters.active !== undefined) params.set("active", String(filters.active));
  if (filters.page) params.set("page", filters.page.toString());
  if (filters.pageSize) params.set("pageSize", filters.pageSize.toString());

  const query = params.toString();
  return apiRequest<AdminPromoCodeListResponse>(
    `/api/admin/promo-codes${query ? `?${query}` : ""}`,
    { token },
  );
}

export function getAdminPromoCodeDetail(token: string | null, codeId: string) {
  return apiRequest<AdminPromoCodeDetailResponse>(
    `/api/admin/promo-codes/${encodeURIComponent(codeId)}`,
    { token },
  );
}

export function createAdminPromoCode(
  token: string | null,
  input: AdminPromoCodeCreateInput,
) {
  return apiRequest<AdminPromoCodeSummary>("/api/admin/promo-codes", {
    method: "POST",
    token,
    body: input,
  });
}

export function deactivateAdminPromoCode(
  token: string | null,
  codeId: string,
  input: AdminPromoCodeStatusInput,
) {
  return apiRequest<AdminPromoCodeSummary>(
    `/api/admin/promo-codes/${encodeURIComponent(codeId)}/deactivate`,
    { method: "POST", token, body: input },
  );
}

export function reactivateAdminPromoCode(
  token: string | null,
  codeId: string,
  input: AdminPromoCodeStatusInput,
) {
  return apiRequest<AdminPromoCodeSummary>(
    `/api/admin/promo-codes/${encodeURIComponent(codeId)}/reactivate`,
    { method: "POST", token, body: input },
  );
}

// ============================================================================
// METRICS
// ============================================================================

export type MetricsRange =
  | "today"
  | "yesterday"
  | "7d"
  | "30d"
  | "90d"
  | "all"
  | "custom";

export type MetricsFilters = {
  feature?: string;
  email?: string;
  userId?: string;
  tier?: string;
  startDate?: string;
  endDate?: string;
};

export type AdminMetricsOverviewResponse = {
  ok: true;
  range: MetricsRange;
  generatedAt: string;
  filters: {
    feature: string | null;
    email: string | null;
    userId: string | null;
    tier: string | null;
  };
  product: {
    totalUsers: number;
    usersByTier: Array<{ tier: string; count: number }>;
    adminUsers: number;
    totalProjects: number;
    totalBlueprints: number;
    generatedBlueprints: number;
    totalToolResults: number;
  };
  accessCodes: {
    totalCodes: number;
    activeCodes: number;
    totalRedemptions: number;
    redemptionsInRange: number;
    topCodes: Array<{
      code: string;
      tierGranted: string;
      usesCount: number;
      maxUses: number | null;
      active: boolean;
    }>;
  };
  purchases: {
    totalEvents: number;
    eventsInRange: number;
    statusCounts: Array<{ status: string; count: number }>;
    providerCounts: Array<{ provider: string; count: number }>;
    recentFailures: Array<{
      id: string;
      provider: string;
      eventType: string | null;
      email: string | null;
      errorMessage: string | null;
      createdAt: string;
    }>;
  };
  aiUsage: {
    totalCalls: number;
    callsInRange: number;
    totalTokens: number;
    inputTokens: number;
    outputTokens: number;
    estimatedTotalCostUsd: string;
    estimatedCostInRange: string;
    statusCounts: Array<{ status: string; count: number }>;
    featureBreakdown: Array<{
      feature: string;
      calls: number;
      totalTokens: number;
      estimatedCostUsd: string;
      avgTokensPerCall: number;
      avgCostPerCall: string;
      failedCalls: number;
    }>;
    modelBreakdown: Array<{
      model: string;
      calls: number;
      totalTokens: number;
      estimatedCostUsd: string;
    }>;
    recentEvents: Array<{
      id: string;
      feature: string;
      model: string;
      totalTokens: number | null;
      estimatedTotalCostUsd: string | null;
      requestStatus: string;
      createdAt: string;
    }>;
  };
  drilldowns: {
    dailyAiUsage: Array<{
      date: string;
      calls: number;
      totalTokens: number;
      estimatedCostUsd: string;
      failedCalls: number;
    }>;
    topUsersByAiCost: Array<{
      userId: string | null;
      email: string | null;
      name: string | null;
      tier: string | null;
      calls: number;
      totalTokens: number;
      estimatedCostUsd: string;
    }>;
    topUsersByBlueprints: Array<{
      userId: string | null;
      email: string | null;
      name: string | null;
      tier: string | null;
      blueprintCount: number;
      generatedBlueprintCount: number;
    }>;
    highCostEvents: Array<{
      id: string;
      userId: string | null;
      email: string | null;
      feature: string;
      model: string;
      totalTokens: number | null;
      estimatedTotalCostUsd: string | null;
      createdAt: string;
    }>;
    aiEvents: Array<{
      id: string;
      userId: string | null;
      email: string | null;
      feature: string;
      model: string;
      inputTokens: number | null;
      outputTokens: number | null;
      totalTokens: number | null;
      estimatedTotalCostUsd: string | null;
      requestStatus: string;
      createdAt: string;
    }>;
    accessCodeRedemptionsByDay: Array<{ date: string; redemptions: number }>;
    purchaseFailuresByDay: Array<{ date: string; failures: number }>;
  };
};

export function getAdminMetricsOverview(
  token: string | null,
  range: MetricsRange = "30d",
  filters: MetricsFilters = {},
) {
  const params = new URLSearchParams({ range });
  if (filters.feature) params.set("feature", filters.feature);
  if (filters.email) params.set("email", filters.email);
  if (filters.userId) params.set("userId", filters.userId);
  if (filters.tier) params.set("tier", filters.tier);
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);

  return apiRequest<AdminMetricsOverviewResponse>(
    `/api/admin/metrics/overview?${params.toString()}`,
    { token },
  );
}
